//カード編集画面-内容タブ
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { TextField, Button, Grid, Container, Box, IconButton, Menu, MenuItem } from '@material-ui/core';
import { FormControlLabel, Checkbox } from '@material-ui/core';
import { Tooltip } from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import QuestionMarkIcon from '@material-ui/icons/HelpOutline';
import { useDispatch } from 'react-redux';

import { CardDb } from '../redux/cardDbSlice';
import { TableAttribute, ImageAttribute, AttributeHavingFont, CardAttributeDb, CardAttributeDbHashType, isAttributeHavingFont, isMultiAttribute, isNondisplayAttribute } from '../redux/cardAttributeDbSlice';
import { CardShapeDb } from '../redux/cardShapeDbSlice';
import { generateImageId } from '../redux/imageDbSlice';
import { ImageData } from '../redux/cardAttributeModelIndex';
import useWindowSize from '@rehooks/window-size';
import useElementDimensions from '../hooks/useElementDimensions';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useLatelyImages from '../hooks/useLatelyImages';
import { deepCopy, FullSizeImageAddType, updateImageDbs } from '../general';
import { initial, getCardSize } from '.';
import CommonCard from './CommonCard';
import { EditCardShapeDbHashType } from '../components/EditDialog';
import FontDialog from '../components/FontDialog';
import EditTableAttributeDialog from '../components/EditTableAttributeDialog';
import ImageBrowserDialog from '../components/ImageBrowserDialog';
import DrawToolDialog from '../components/DrawToolDialog';
import TrimmingDialog from '../components/TrimmingDialog';
import ItemDescriptionDialog from '../components/ItemDescriptionDialog';

const CardAreaWrapper = styled.div`
  padding-top: 8px;
  padding-left: 8px;
  height: 96%;
  overflow: hidden auto;
`
const ItemField = styled(TextField)`
  width: 100%;
`

const getImageData = (imageModel: FullSizeImageAddType): ImageData => {
  const imageId = imageModel.id;
  const originWidth = imageModel.width;
  const originHeight = imageModel.height;
  const trimXFrom = 0;
  const trimXTo = imageModel.width;
  const trimYFrom = 0;
  const trimYTo = imageModel.height;

  return { imageId, originWidth, originHeight, trimXFrom, trimXTo, trimYFrom, trimYTo };
}

/* 内容タブ-カード表示 */
type CardAreaProps = {
  cardId: string;
  card: CardDb;
  setCard: (card: CardDb) => void;
  ids: string[];
  values: (CardAttributeDb | CardShapeDb)[];
  setAttributes: (items: CardAttributeDbHashType) => void;
  editingId: string | undefined;
  splitUpdated: boolean;
}
const CardArea: React.FC<CardAreaProps> = ({ cardId, card, setCard, ids, values, setAttributes, editingId, splitUpdated }) => {
  const areaWidth = useElementDimensions('card-area-wrapper', splitUpdated).width;
  const padding = 8;
  const cardWidth = getCardSize(card).width;
  const previewWidth = areaWidth ? Math.min(areaWidth, cardWidth) - padding : initial.cardWidth - padding;
  const previewScale = previewWidth / cardWidth;

  return(
    <CardAreaWrapper id="card-area-wrapper">
      <div style={{ height: getCardSize(card).height * previewScale }}>
      <CommonCard cardId={cardId}
        previews={{
          previewScale,
          card,
          setCard,
          ids,
          values,
          setAttributes,
          editingId
        }}
      />
      </div>
    </CardAreaWrapper>
  )
}

/* 内容タブ-各項目を表示する */
type ItemAreaProps = {
  id: string;
  attribute: CardAttributeDb;
  changeContent: (ev: any) => void;
  changeImageContent: (image: ImageData) => void;
  editTableClicked: (item: TableAttribute) => void;
  setEditingId: (id: string | undefined) => void;
  onImageBrowser: () => void;
  onDrawTool: () => void;
  onTrimming: (data: ImageData) => void;
}
const ItemArea: React.FC<ItemAreaProps> = ({ id, attribute, changeContent, changeImageContent, editTableClicked, setEditingId, onImageBrowser, onDrawTool, onTrimming }) => {
  const dispatch = useDispatch();
  const userId = useCurrentUserId();
  const [imageAttributeAnchorEl, setImageAttributeAnchorEl] = useState<null | HTMLElement>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | undefined>(undefined);
  const [latelyIds, , latelyImagesId] = useLatelyImages();

  useEffect(() => {
    if (!imgElement) return;
    const width = imgElement.width;
    const height = imgElement.height;
    if (width === 0 && height === 0) return;
    const imageId = generateImageId();
    updateImageDbs({ dispatch, imageId, dataUrlBase64: imgElement.src, userId, latelyIds, latelyImagesId });
    changeImageContent(getImageData({ id: imageId, width, height }));
    setImgElement(undefined);
  }, [imgElement])

  const handleSingleBlur = () => {
    setEditingId(undefined);
  }
  const handleMultiBlur = () => {
    setEditingId(undefined);
  }
  const imageAttributeHandleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setImageAttributeAnchorEl(event.currentTarget);
  }
  const imageAttributeHandleClose = () => {
    setImageAttributeAnchorEl(null);
  }
  const imageBrowserOpen = () => {
    imageAttributeHandleClose();
    onImageBrowser();
  }
  const drawToolOpen = () => {
    imageAttributeHandleClose();
    onDrawTool();
  }
  const setImageDefault = (target: ImageAttribute) => {
    imageAttributeHandleClose();
    changeImageContent(target.defaultImage);
  }
  const openUserFileSelector = (htmlId: string) => {
    imageAttributeHandleClose();
    const selector = document.getElementById(htmlId);
    if (!selector) return;
    selector.click();
  }
  const getFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (!ev.target.files) return;
    const file = ev.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      const dataUrlBase64 = reader.result;
      const img = new Image();
      img.src = dataUrlBase64;
      img.style.display = 'none';
      img.remove();

      setImgElement(img);
    }
  }

  return(
    <>
      { attribute.cardAttributeModel === 'single' &&// 単一行アイテムの場合
        <ItemField variant="outlined"
          defaultValue={attribute.content}
          label={attribute.name}
          placeholder={attribute.hint}
          onFocus={() => setEditingId(id)}
          onChange={changeContent}
          onBlur={handleSingleBlur}
        />
      }
      { attribute.cardAttributeModel === 'multi' &&// 複数行アイテムの場合
        <ItemField variant="outlined"
          multiline minRows={8} maxRows={8}
          defaultValue={attribute.content}
          label={attribute.name}
          placeholder={attribute.hint}
          onFocus={() => setEditingId(id)}
          onChange={changeContent}
          onBlur={handleMultiBlur}
        />
      }
      { attribute.cardAttributeModel === 'table' &&// 表項目の場合
        <Box>
          <Button variant="outlined" size="large" color="primary" style={{padding: '12px'}}
                  onClick={() => {
                    editTableClicked(attribute);
                    setEditingId(id);
                  }}>
            { attribute.name } の表を編集する
          </Button>
        </Box>
      }
      { attribute.cardAttributeModel === 'image' &&// 画像項目の場合
        <Box>
          <Button variant="outlined" size="large" color="primary" style={{padding: '12px', marginRight: 16}}
            aria-controls="image-menu"
            onClick={(ev) => {
              imageAttributeHandleClick(ev);
              setEditingId(id);
            }}
          >
            { attribute.name } の画像を設定する
          </Button>
          <Menu id="image-menu" anchorEl={imageAttributeAnchorEl} keepMounted
            open={Boolean(imageAttributeAnchorEl)}
            onClose={imageAttributeHandleClose}
          >
            <MenuItem onClick={imageBrowserOpen}>画像ブラウザ...</MenuItem>
            <MenuItem onClick={() => openUserFileSelector('user-file-selector')} id="user-file-selector-button">
              <input type="file" accept="image/png, image/jpeg" id="user-file-selector" onChange={(ev) => getFile(ev)} style={{display: 'none'}} />ファイルから挿入...
            </MenuItem>
            <MenuItem onClick={drawToolOpen}>絵を描いて挿入...</MenuItem>
            <MenuItem onClick={() => setImageDefault(attribute)}>デフォルトに戻す</MenuItem>
          </Menu>
          <Button variant="outlined" size="large" color="primary" style={{padding: '12px'}}
            onClick={() => {
              onTrimming(attribute.content);
              setEditingId(id);
            }}
          >
            トリミングする
          </Button>
        </Box>
      }
      { attribute.cardAttributeModel === 'nondisplay' &&// 非表示アイテムの場合
        <ItemField variant="outlined"
          multiline minRows={2} maxRows={8}
          defaultValue={attribute.content}
          label={attribute.name}
          placeholder={attribute.hint}
          onFocus={() => setEditingId(id)}
          onChange={changeContent}
          onBlur={handleMultiBlur}
        />
      }
    </>
  )
}

/* 内容タブ-フォント設定ボタン */
type FontAreaProps = {
  editFontClicked: () => void;
  setEditingId: () => void;
}
const FontArea: React.FC<FontAreaProps> = ({editFontClicked, setEditingId}) => {
  return(
    <Tooltip title="フォント設定">
      <IconButton onClick={() => {
        editFontClicked();
        setEditingId();
        }}><SettingsIcon/>
      </IconButton>
    </Tooltip>
  )
}
/* 内容タブ-アイテム説明表示ボタン */
type DescriptionAreaProps = {
  displayClicked: () => void;
}
const DescriptionArea: React.FC<DescriptionAreaProps> = ({displayClicked}) => {
  return(
    <Tooltip title="アイテム説明">
      <IconButton onClick={() => {
        displayClicked();
        }}><QuestionMarkIcon/>
      </IconButton>
    </Tooltip>
  )
}
//内容タブ
type EditCardContentsProps = {
  cardId: string;
  card: CardDb;
  setCard: (card: CardDb) => void;
  attributes: CardAttributeDbHashType;
  setAttributes: (items: CardAttributeDbHashType) => void;
  shapes: EditCardShapeDbHashType;
  splitUpdated: boolean;
}
const EditCardContents: React.FC<EditCardContentsProps> = ({ cardId, card, setCard, attributes, setAttributes, shapes, splitUpdated }) => {
  const attributeIds = Object.keys(attributes);
  attributeIds.sort((a, b) => attributes[a].order - attributes[b].order);
  const shapeIds = Object.keys(shapes).filter(id => shapes[id]);
  const itemIds = attributeIds.concat(shapeIds);
  itemIds.sort((a, b) => {
    const aValue = (shapes[a] ? shapes[a] : attributes[a]) as CardAttributeDb | CardShapeDb;
    const bValue = (shapes[b] ? shapes[b] : attributes[b]) as CardAttributeDb | CardShapeDb;
    return bValue.z - aValue.z;
  })
  const values: (CardAttributeDb | CardShapeDb)[] = [];
  itemIds.forEach(id => {
    const value = shapes[id] ? shapes[id] : attributes[id];
    if (value) values.push(value);
  });
  type FontActionState = {show: boolean, item?: AttributeHavingFont, id?: string}
  const [fontAction, setFontAction] = useState<FontActionState>({show: false})
  type DescriptionActionState = {show: boolean, content?: string, id?: string}
  const [descriptionAction, setDescriptionAction] = useState<DescriptionActionState>({show: false})
  type EditTableActionState = {show: boolean, item?: TableAttribute, id?: string};
  const [editTableAction, setEditTableAction] = useState<EditTableActionState>({show: false});
  const [imageBrowserAction, setImageBrowserAction] = useState({ show: false });
  const [drawToolAction, setDrawToolAction] = useState({ show: false });
  type TrimmingActionType = { show: boolean; data?: ImageData }
  const [trimmingAction, setTrimmingAction] = useState<TrimmingActionType>({ show: false });
  const windowSize = useWindowSize()
  const workHeight = windowSize.innerHeight - (64 * 2) - 110
  const [editingId, setEditingId] = useState<string>();

  const changeContent = (id: string, ev: any): void => {
    const newContent = ev.target.value;
    const newAttributes = deepCopy(attributes);
    const targetAttribute = newAttributes[id];
    if (isAttributeHavingFont(targetAttribute) || isNondisplayAttribute(targetAttribute)) {
      targetAttribute.content = newContent;
      newAttributes[id] = targetAttribute;
      setAttributes(newAttributes);
    }
  }
  const changeItem = (id: string | undefined, item: CardAttributeDb): void => {
    if (typeof id !== 'string') return;
    const newAttributes = deepCopy(attributes);
    newAttributes[id] = item;
    // wait rendering new items
    const async = () => new Promise((resolve) => resolve(''))
    async()
    .then((value) => setAttributes(newAttributes))
  }
  const changeImageContent = (image: ImageData) => {
    if (!editingId) return;
    const attribute = deepCopy(attributes[editingId]);
    if (!attribute || attribute.cardAttributeModel !== 'image') return;
    attribute.content = image;
    changeItem(editingId, attribute);
  }
  const changeIsMarkdown = (id: string, value: boolean): void => {
    const newAttributes = deepCopy(attributes);
    const targetAttribute = newAttributes[id];
    if (!isMultiAttribute(targetAttribute)) return;
    targetAttribute.isMarkdown = value;
    newAttributes[id] = targetAttribute;
    setAttributes(newAttributes);
  }

  return (
    <>
      <Grid container spacing={0}>
        <Grid item xs={3} style={{ height: workHeight }}>
          <CardArea cardId={cardId} card={card} setCard={setCard}
            ids={itemIds} values={values} setAttributes={setAttributes}
            editingId={editingId}
            splitUpdated={splitUpdated}
          /> 
        </Grid>
        <Grid item xs={9}>
          <Container style={{ height: workHeight, overflow: 'auto' }}>
            { attributeIds.map(id => {
              const attribute = attributes[id];
              return (
                <Box key={id} mt={2} mb={4} pb={2}>
                  <Grid container spacing={0}>
                    <Grid item xs={10} style={{ paddingRight: 8 }}>
                      <ItemArea id={id} attribute={attribute}
                        changeContent={(ev) => changeContent(id, ev)}
                        changeImageContent={changeImageContent}
                        editTableClicked={(item: TableAttribute) => setEditTableAction({show: true, item: item, id: id})}
                        setEditingId={(id: string | undefined) => setEditingId(id)}
                        onImageBrowser={() => setImageBrowserAction({ show: true })}
                        onDrawTool={() => setDrawToolAction({ show: true })}
                        onTrimming={(data) => setTrimmingAction({ show: true, data: data })} />
                    </Grid>
                    <Grid item xs={2}>
                      { isAttributeHavingFont(attribute) &&
                        <FontArea editFontClicked={() => setFontAction({show: true, item: attribute, id: id})}
                          setEditingId={() => setEditingId(id)} />
                      }
                      { attribute.description.display &&
                        <DescriptionArea displayClicked={() => setDescriptionAction({show: true, content: attribute.description.content, id: id})}/>
                      }
                      { isMultiAttribute(attribute) &&
                        <FormControlLabel label="マークダウン" style={{ marginLeft: 3 }}
                          control={
                            <Checkbox checked={attribute.isMarkdown} color="primary"
                              onChange={(ev) => changeIsMarkdown(id, ev.currentTarget.checked)} />
                          } />
                      }
                    </Grid>
                  </Grid>
                </Box>
              );
            }) }
          </Container>
        </Grid>
      </Grid>
      {/* フォント設定画面を開く */}
      { fontAction.item &&
        <FontDialog open={fontAction.show}
          onClose={() => setFontAction({show: false})}
          item={fontAction.item}
          changeItem={(item: AttributeHavingFont) => changeItem(fontAction.id, item)} />
      }
      {/* アイテム説明表示画面を開く */}
      <ItemDescriptionDialog open={descriptionAction.show}
          onClose={() => setDescriptionAction({show: false})}
          content={descriptionAction.content}
      />
      {/* 表を編集する画面を開く */}
      { editTableAction.item &&
        <EditTableAttributeDialog open={editTableAction.show}
          onClose={() => setEditTableAction({show: false})}
          item={editTableAction.item}
          changeItem={(item: any) => changeItem(editTableAction.id, item)} />
      }
      <ImageBrowserDialog open={imageBrowserAction.show} onClose={() => setImageBrowserAction({ show: false })}
        onAddAttribute={(kind, image) => changeImageContent(getImageData(image))}
      />
      <DrawToolDialog open={drawToolAction.show} onClose={() => setDrawToolAction({ show: false })}
        onSave={(image) => changeImageContent(getImageData(image))}
      />
      { trimmingAction.data &&
        <TrimmingDialog open={trimmingAction.show} onClose={() => setTrimmingAction({ show: false })}
          data={trimmingAction.data} changeData={changeImageContent} />
      }
    </>
  );
}

export default EditCardContents;
